import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, MapPin, Calendar, CheckCircle2, Clock3, Archive, Pencil } from "lucide-react";
import CountryPrompt from "@/components/CountryPrompt";

const COUNTRY_LABELS = { AU: "Australia", NZ: "New Zealand", AU_NZ: "Australia + New Zealand" };

const SEL_KEY = (c) => `tsh-conf-sel-v2-${c}`;
const fmtDay = (iso) => (iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-AU", { day: "numeric", month: "short", timeZone: "UTC" }) : "");

function TicketToggle({ conf, tid, onSet }) {
  const purchased = conf.my_status?.ticket_status === "purchased";
  const btn = (on) => `px-3 py-1.5 text-xs rounded-full transition-colors ${on}`;
  return (
    <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1" data-testid={`ticket-toggle-${tid}`}>
      <button onClick={() => onSet(conf.id, "not_yet")} data-testid={`ticket-not-yet-${tid}`}
        className={btn(!purchased ? "bg-[#0A1F44] text-white" : "text-slate-600 hover:text-slate-900")}>
        <Clock3 className="w-3 h-3 inline mr-1 -mt-0.5" /> Not yet
      </button>
      <button onClick={() => onSet(conf.id, "purchased")} data-testid={`ticket-purchased-${tid}`}
        className={btn(purchased ? "bg-[#C9A961] text-[#0A1F44] font-semibold" : "text-slate-600 hover:text-slate-900")}>
        <CheckCircle2 className="w-3 h-3 inline mr-1 -mt-0.5" /> Purchased
      </button>
    </div>
  );
}

function TicketCard({ cc, conf, open, tid, canWrite, onSet }) {
  const purchased = conf.my_status?.ticket_status === "purchased";
  return (
    <Card className={`p-5 relative overflow-hidden ${open ? "bg-slate-50/60" : ""}`} data-testid={open ? `open-${conf.id}` : `current-${cc}`}>
      <div className="absolute right-4 top-4 text-[10px] uppercase tracking-[0.22em] text-[#C9A961] font-semibold">{COUNTRY_LABELS[cc]}</div>
      {open && <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">{conf.ended ? "Previous event" : "Happening now"}</div>}
      <div className="font-serif text-xl text-[#0A1F44] pr-20">{conf.name}</div>
      {open && conf.ticket_update_until && (
        <div className="mt-1 text-sm text-emerald-700" data-testid={`open-label-${conf.id}`}>
          {conf.name} — ticket updates open until {fmtDay(conf.ticket_update_until)}
        </div>
      )}
      <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
        <Calendar className="w-4 h-4 text-[#C9A961]" /><span>{fmtRange(conf.starts_on, conf.ends_on)}</span>
      </div>
      {conf.city && (
        <div className="mt-1 flex items-center gap-2 text-sm text-slate-600"><MapPin className="w-4 h-4 text-[#C9A961]" /><span>{conf.city}</span></div>
      )}
      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-wider text-slate-500">Ticket status</div>
        {canWrite && conf.updates_open !== false ? <TicketToggle conf={conf} tid={tid} onSet={onSet} /> : (
          <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${purchased ? "bg-[#C9A961]/15 text-[#6B5A1D]" : "bg-slate-100 text-slate-600"}`}
            data-testid={`ticket-readonly-${tid}`}>
            {purchased ? <CheckCircle2 className="w-3 h-3" /> : <Clock3 className="w-3 h-3" />}
            {purchased ? "Purchased" : "Not yet"}
          </span>
        )}
      </div>
    </Card>
  );
}

function fmtRange(startIso, endIso) {
  if (!startIso && !endIso) return "Dates TBC";
  try {
    const s = startIso ? new Date(startIso) : null;
    const e = endIso ? new Date(endIso) : null;
    const opts = { day: "numeric", month: "short", year: "numeric" };
    if (s && e) {
      // Same month: "12–14 Feb 2027"
      if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
        return `${s.getDate()}–${e.getDate()} ${s.toLocaleDateString("en-AU", { month: "short", year: "numeric" })}`;
      }
      return `${s.toLocaleDateString("en-AU", opts)} – ${e.toLocaleDateString("en-AU", opts)}`;
    }
    return (s || e).toLocaleDateString("en-AU", opts);
  } catch {
    return "Dates TBC";
  }
}

export default function Conferences() {
  const { user, isReadOnly, scopes } = useAuth();
  const isAdmin = user?.role === "admin";
  const isOversight = user?.role === "oversight";
  const canWriteOwnTicket = !isReadOnly && !isOversight;
  const multiCountry = ["admin", "oversight", "platinum"].includes(user?.role);

  const [current, setCurrent] = useState({ au: null, nz: null, countries: [], primary: "AU" });
  const [country, setCountry] = useState(null);
  const [list, setList] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selected, setSelected] = useState(null);
  const [report, setReport] = useState(null);
  const [scope, setScope] = useState("personal");
  const [showPurchasedNames, setShowPurchasedNames] = useState(false);
  const [showNotYetNames, setShowNotYetNames] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null => create

  const currentRef = useRef(null);
  const listCountry = useRef(null);

  const loadCurrent = async () => {
    const { data } = await api.get("/conferences/current");
    const subjectChanged = currentRef.current?.subject_id !== data?.subject_id;
    currentRef.current = data;
    setCurrent(data);
    if (data?.primary) setCountry((prev) => (subjectChanged || !prev ? data.primary : prev));
  };

  // Default = the upcoming event for this region; an explicit pick is remembered for the browser session (per viewed person).
  const selKey = (c) => SEL_KEY(`${currentRef.current?.subject_id || "me"}-${c}`);
  const pickFor = (c, data) => {
    const saved = sessionStorage.getItem(selKey(c));
    const regional = currentRef.current?.[c.toLowerCase()]?.id;
    return data.find((d) => d.id === saved) || data.find((d) => d.id === regional) || data[0] || null;
  };

  const loadList = async (c) => {
    const { data } = await api.get("/conferences", {
      params: { country: c, include_archived: includeArchived },
    });
    const sameCountry = listCountry.current === c;
    listCountry.current = c;
    setList(data);
    setSelected((prev) => (sameCountry && prev && data.find((d) => d.id === prev.id)) || pickFor(c, data));
  };

  const choose = (c) => {
    sessionStorage.setItem(selKey(country), c.id);
    setSelected(c); setShowPurchasedNames(false); setShowNotYetNames(false);
  };

  useEffect(() => { loadCurrent().catch(() => {}); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (country) loadList(country).catch(() => {}); /* eslint-disable-next-line */ }, [country, includeArchived]);

  useEffect(() => {
    if (!selected) { setReport(null); return; }
    api.get(`/conferences/${selected.id}/report`, { params: { scope } })
      .then((r) => setReport(r.data))
      .catch(() => setReport(null));
  }, [selected, scope]);

  const setMyTicket = async (confId, next) => {
    try {
      await api.post(`/conferences/${confId}/my-status`, { ticket_status: next });
      toast.success(next === "purchased" ? "Ticket marked purchased" : "Ticket marked not yet");
      await Promise.all([loadCurrent(), loadList(country)]);
      if (selected?.id === confId) {
        const { data } = await api.get(`/conferences/${confId}/report`, { params: { scope } });
        setReport(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleArchive = async (conf) => {
    try {
      await api.patch(`/conferences/${conf.id}`, { archived: !conf.archived });
      toast.success(conf.archived ? "Unarchived" : "Archived");
      await Promise.all([loadCurrent(), loadList(country)]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const openCreate = () => { setEditing(null); setEditOpen(true); };
  const openEdit = (c) => { setEditing(c); setEditOpen(true); };

  // Current conference cards shown at the top for everyone.
  // Combined (AU_NZ) conferences surface in BOTH slots server-side; dedupe so
  // we show a single "Australia + New Zealand" card instead of the same card
  // twice for Platinum/Admin/Oversight.
  const currentCards = useMemo(() => {
    const out = [];
    const seen = new Set();
    const push = (cc, conf, open) => {
      if (!conf) return;
      if (seen.has(conf.id)) return;
      seen.add(conf.id);
      // When a combined conf fills the slot, badge it as its own country.
      const badge = conf.country === "AU_NZ" ? "AU_NZ" : cc;
      out.push({ cc: badge, conf, open, tid: open ? conf.id : cc });
    };
    const order = current.order || [current.primary || "AU", current.primary === "NZ" ? "AU" : "NZ"];
    order.forEach((cc) => push(cc, current[cc.toLowerCase()]));
    order.forEach((cc) => (current[`open_${cc.toLowerCase()}`] || []).forEach((c) => push(cc, c, true)));
    return out;
  }, [current]);

  return (
    <div className="space-y-6" data-testid="conferences-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">Conferences</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#0A1F44] mt-1">Conference tickets</h1>
          <p className="text-sm text-slate-500 mt-1">
            Track your ticket for the next conference. Switch to Purchased when you buy it.
          </p>
        </div>
        {isAdmin && !isReadOnly && (
          <Button onClick={openCreate} data-testid="add-conf-btn"
                  className="rounded-full bg-[#0A1F44] text-white">
            <Plus className="w-4 h-4 mr-1" /> Add conference
          </Button>
        )}
      </div>

      {/* Current / Next conferences — simple cards for everyone */}
      <CountryPrompt onSaved={() => loadCurrent().then(() => setCountry(currentRef.current?.primary || null)).catch(() => {})} />

      <section className="grid gap-4" data-testid="current-conferences">
        {currentCards.length === 0 && (
          <Card className="p-5 text-sm text-slate-500" data-testid="no-current">
            No active conference right now. Please check back soon.
          </Card>
        )}
        {currentCards.map(({ cc, conf, open, tid }) => (
          <TicketCard key={conf.id} cc={cc} conf={conf} open={open} tid={tid}
            canWrite={canWriteOwnTicket} onSet={setMyTicket} />
        ))}
      </section>

      {/* Reporting + history for Leader / Platinum / Admin / Oversight */}
      {!(user?.role === "partner") && (
        <Card className="p-5" data-testid="conference-reporting">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
            <div>
              <h2 className="font-serif text-lg text-[#0A1F44]">Reporting &amp; history</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {multiCountry
                  ? "Switch between Australia and New Zealand conferences."
                  : "Conferences for your country, within your team scope."}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {multiCountry && (
                <div className="inline-flex rounded-full bg-slate-100 p-1" data-testid="country-toggle">
                  {["AU", "NZ"].map((cc) => (
                    <button
                      key={cc}
                      onClick={() => setCountry(cc)}
                      data-testid={`country-${cc}`}
                      className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                        country === cc ? "bg-[#0A1F44] text-white" : "text-slate-700 hover:bg-white"
                      }`}
                    >
                      {COUNTRY_LABELS[cc]}
                    </button>
                  ))}
                </div>
              )}
              <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" data-testid="archived-toggle">
                <input
                  type="checkbox"
                  checked={includeArchived}
                  onChange={(e) => setIncludeArchived(e.target.checked)}
                  className="accent-[#0A1F44]"
                />
                Include archived
              </label>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap mb-4">
            {list.length === 0 && <div className="text-sm text-slate-500">No conferences in this view.</div>}
            {list.map((c) => (
              <button
                key={c.id}
                onClick={() => choose(c)}
                data-testid={`conf-tab-${c.id}`}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  selected?.id === c.id
                    ? "bg-[#0A1F44] text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {c.name}
                {c.archived && <span className="ml-1 text-[10px] uppercase tracking-wider opacity-75">archived</span>}
                {!c.archived && c.ended && <span className="ml-1 text-[10px] uppercase tracking-wider opacity-75" data-testid={`conf-ended-${c.id}`}>{c.updates_open ? `updates open until ${fmtDay(c.ticket_update_until)}` : "closed"}</span>}
              </button>
            ))}
          </div>

          {selected && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <div className="text-sm text-slate-700">
                  <span className="font-medium text-[#0A1F44]">{selected.name}</span>
                  <span className="text-slate-400 mx-2">·</span>
                  <span>{fmtRange(selected.starts_on, selected.ends_on)}</span>
                  {selected.city && <><span className="text-slate-400 mx-2">·</span><span>{selected.city}</span></>}
                  {selected.ticket_update_until && !selected.promotable && <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${selected.updates_open ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid="conf-update-window">
                    {selected.updates_open ? `Ticket updates open until ${fmtDay(selected.ticket_update_until)}` : `Ticket updates closed ${fmtDay(selected.ticket_update_until)} · read-only`}
                  </span>}
                </div>
                <div className="flex items-center gap-2">
                  <Select value={scope} onValueChange={setScope}>
                    <SelectTrigger className="h-9 w-32" data-testid="conf-scope"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(scopes || ["personal"]).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {isAdmin && !isReadOnly && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => openEdit(selected)} data-testid="edit-conf">
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => toggleArchive(selected)} data-testid="toggle-archive">
                        <Archive className="w-3.5 h-3.5 mr-1" /> {selected.archived ? "Unarchive" : "Archive"}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {report && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="conf-report-grid">
                  <ReportTile label="Total people" value={report.counts.total} tone="muted" />
                  <ReportTile
                    label="Purchased"
                    value={report.counts.purchased}
                    tone="gold"
                    testid="bucket-purchased"
                    onClick={() => setShowPurchasedNames((v) => !v)}
                    expanded={showPurchasedNames}
                  />
                  <ReportTile
                    label="Not yet"
                    value={report.counts.not_yet}
                    tone="navy"
                    testid="bucket-not_yet"
                    onClick={() => setShowNotYetNames((v) => !v)}
                    expanded={showNotYetNames}
                  />
                </div>
              )}

              {report && showPurchasedNames && (
                <PeopleList title="Purchased" people={report.people.purchased} testid="names-purchased" />
              )}
              {report && showNotYetNames && (
                <PeopleList title="Not yet" people={report.people.not_yet} testid="names-not_yet" />
              )}
            </>
          )}
        </Card>
      )}

      {/* Admin: edit/create dialog */}
      <ConferenceEditor
        open={editOpen}
        onOpenChange={setEditOpen}
        conference={editing}
        defaultCountry={country}
        onSaved={async () => {
          setEditOpen(false);
          await Promise.all([loadCurrent(), loadList(country)]);
        }}
      />
    </div>
  );
}

function ReportTile({ label, value, tone, onClick, expanded, testid }) {
  const base = "rounded-lg border p-3 text-left transition-colors";
  const styles =
    tone === "gold"
      ? "border-[#C9A961]/40 bg-[#C9A961]/5 hover:bg-[#C9A961]/10"
      : tone === "navy"
      ? "border-[#0A1F44]/15 bg-[#0A1F44]/[0.03] hover:bg-[#0A1F44]/[0.06]"
      : "border-slate-200 bg-white";
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      className={`${base} ${styles} ${onClick ? "cursor-pointer" : ""}`}
      onClick={onClick}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wider text-[#C9A961] font-semibold flex items-center justify-between">
        <span>{label}</span>
        {onClick && <span className="text-slate-400">{expanded ? "Hide" : "Show"}</span>}
      </div>
      <div className="font-serif text-2xl text-[#0A1F44] mt-1 tabular-nums">{value}</div>
    </Tag>
  );
}

function PeopleList({ title, people, testid }) {
  if (!people || people.length === 0) {
    return (
      <div className="mt-3 rounded-lg border border-dashed border-slate-200 p-3 text-xs text-slate-500" data-testid={testid}>
        No one in {title.toLowerCase()}.
      </div>
    );
  }
  return (
    <div className="mt-3 rounded-lg border border-slate-200 p-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-[#C9A961] font-semibold mb-2">{title} · {people.length}</div>
      <ul className="grid sm:grid-cols-2 gap-y-1 gap-x-4 text-sm">
        {people.map((p) => (
          <li key={p.id} className="truncate text-slate-800" data-testid={`${testid}-row-${p.id}`}>
            {p.name}
            <span className="text-slate-400 text-xs ml-1.5">· {p.upline_team || "—"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ConferenceEditor({ open, onOpenChange, conference, defaultCountry, onSaved }) {
  const isEdit = Boolean(conference);
  const [form, setForm] = useState(() => blank(defaultCountry));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(conference
        ? {
            name: conference.name || "",
            country: conference.country || defaultCountry || "AU",
            year: conference.year || new Date().getFullYear(),
            starts_on: conference.starts_on || "",
            ends_on: conference.ends_on || "",
            city: conference.city || "",
            active: conference.active ?? true,
            archived: conference.archived ?? false,
          }
        : blank(defaultCountry));
    }
  }, [open, conference, defaultCountry]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (!["AU", "NZ", "AU_NZ"].includes(form.country)) { toast.error("Country must be AU, NZ or AU_NZ"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        country: form.country,
        year: parseInt(form.year, 10),
        starts_on: form.starts_on || null,
        ends_on: form.ends_on || null,
        city: form.city?.trim() || null,
        active: !!form.active,
      };
      if (isEdit) {
        await api.patch(`/conferences/${conference.id}`, { ...payload, archived: !!form.archived });
      } else {
        await api.post("/conferences", payload);
      }
      toast.success(isEdit ? "Conference updated" : "Conference created");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif text-[#0A1F44]">
            {isEdit ? "Edit conference" : "Add conference"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="conf-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Country</Label>
              <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                <SelectTrigger data-testid="conf-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="AU">Australia</SelectItem>
                  <SelectItem value="NZ">New Zealand</SelectItem>
                  <SelectItem value="AU_NZ">Combined (AU + NZ)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Year</Label>
              <Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="conf-year" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start date</Label>
              <Input type="date" value={form.starts_on || ""} onChange={(e) => setForm({ ...form, starts_on: e.target.value })} data-testid="conf-start" />
            </div>
            <div>
              <Label>End date</Label>
              <Input type="date" value={form.ends_on || ""} onChange={(e) => setForm({ ...form, ends_on: e.target.value })} data-testid="conf-end" />
            </div>
          </div>
          <div>
            <Label>City</Label>
            <Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="conf-city" />
          </div>
          <div className="flex items-center gap-4 pt-1 text-sm">
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                data-testid="conf-active"
                className="accent-[#0A1F44]"
              />
              Active (current/next)
            </label>
            {isEdit && (
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.archived}
                  onChange={(e) => setForm({ ...form, archived: e.target.checked })}
                  data-testid="conf-archived"
                  className="accent-[#0A1F44]"
                />
                Archived
              </label>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="conf-save" className="bg-[#0A1F44] text-white rounded-full">
            {busy ? "Saving…" : isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function blank(country) {
  return {
    name: "",
    country: country || "AU",
    year: new Date().getFullYear(),
    starts_on: "",
    ends_on: "",
    city: "",
    active: true,
    archived: false,
  };
}
